import React, { useState, useRef, useEffect } from 'react';
import { API_BASE_URL, API_URL } from "../config";
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import SimpleCaptcha from '../components/SimpleCaptcha';
import { 
  FaUser, FaBuilding, FaEnvelope, FaPhone, FaLock,
  FaIdCard, FaFileAlt, FaUpload, FaCheckCircle,
  FaTruck, FaTrailer, FaUserTie, FaShieldAlt,
  FaMapMarkerAlt, FaRegFilePdf, FaImage,
  FaInfoCircle, FaArrowRight, FaSpinner,
  FaPlus, FaTrash, FaCamera, FaGlobe, FaBriefcase,
  FaCalendarAlt, FaTools, FaWrench, FaClock,
  FaRoad, FaUniversity, FaMoneyBillWave, FaUsers,
  FaQuoteRight, FaBuilding as FaBuildingIcon,
  FaTimes, FaVideo, FaStop, FaRedo, FaEye, FaEyeSlash,
  FaEnvelope as FaEnvelopeIcon
} from 'react-icons/fa';

function SubbieSignup() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [captchaValid, setCaptchaValid] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  
  // ✅ Success state
  const [showSuccess, setShowSuccess] = useState(false);
  const [submittedCompany, setSubmittedCompany] = useState('');
  const [submittedEmail, setSubmittedEmail] = useState('');
  
  // ✅ Camera state
  const [showCamera, setShowCamera] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  // ✅ Password validation state
  const [passwordErrors, setPasswordErrors] = useState({
    minLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    hasSpecial: false
  });
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    // Step 1: Company Info
    company_name: '',
    trading_name: '',
    registration_number: '',
    vat_number: '',
    company_address: '',
    postal_address: '',
    company_phone: '',
    company_fax: '',
    company_website: '',
    years_in_business: '',
    operating_areas: '',
    contact_person: '',
    contact_email: '',
    contact_phone: '',
    position: '',
    
    // Step 2: Documents
    cipc_document: null,
    cipc_annual_return: null,
    shareholder_register: null,
    git_insurance: null,
    public_liability: null,
    operator_card: null,
    bank_confirmation: null,
    bank_statement: null,
    tax_clearance: null,
    bbbee_certificate: null,
    
    // ✅ INSURANCE DETAILS
    git_insurance_provider: '',
    git_policy_number: '',
    git_insurance_expiry: '',
    git_cover_amount: '',
    public_liability_provider: '',
    public_liability_policy_number: '',
    public_liability_expiry: '',
    public_liability_cover_amount: '',
    tax_clearance_expiry: '',
    
    // Banking Details
    bank_name: '',
    bank_branch: '',
    bank_account_number: '',
    bank_account_type: 'business',
    bank_branch_code: '',
    
    // References
    reference_1_name: '',
    reference_1_company: '',
    reference_1_phone: '',
    reference_1_email: '',
    reference_2_name: '',
    reference_2_company: '',
    reference_2_phone: '',
    reference_2_email: '',
    
    // Step 3: ID & Liveness
    id_number: '',
    id_document: null,
    liveness_photo: null,
    
    // Step 4: Fleet
    total_tractors: 0,
    total_trailers: 0,
    total_drivers: 0,
    trucks: [],
    trailers: [],
    drivers: [],
    
    // Fleet Capabilities
    has_own_workshop: false,
    has_24hr_breakdown: false,
    has_tracking_system: false,
    preferred_route: '',
    
    // Step 5: Account
    password: '',
    confirm_password: '',
    accept_terms: false
  });

  const [documentNames, setDocumentNames] = useState({
    cipc_document: '',
    cipc_annual_return: '',
    shareholder_register: '',
    git_insurance: '',
    public_liability: '',
    operator_card: '',
    bank_confirmation: '',
    bank_statement: '',
    tax_clearance: '',
    bbbee_certificate: '',
    id_document: '',
    liveness_photo: ''
  });

  // ============================================
  // ✅ CAMERA FUNCTIONS - FIXED
  // ============================================
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'user', 
          width: { ideal: 640 }, 
          height: { ideal: 480 },
          aspectRatio: 4/3
        },
        audio: false
      });
      
      setCameraStream(stream);
      setShowCamera(true);
      setCapturedImage(null);
      
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(err => {
            console.error('Video play error:', err);
          });
        }
      }, 100);
      
    } catch (err) {
      console.error('Camera error:', err);
      toast.error('Unable to access camera. Please check permissions and try again.');
      setShowCamera(false);
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setShowCamera(false);
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      canvas.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], `selfie-${Date.now()}.jpg`, { type: 'image/jpeg' });
          setFormData(prev => ({ ...prev, liveness_photo: file }));
          setDocumentNames(prev => ({ ...prev, liveness_photo: file.name }));
          setCapturedImage(URL.createObjectURL(blob));
          toast.success('📸 Selfie captured successfully!');
          stopCamera();
        }
      }, 'image/jpeg', 0.9);
    }
  };

  // ✅ FIXED: Properly saves the captured photo
  const useCapturedPhoto = () => {
    // The photo is already saved in formData.liveness_photo from capturePhoto
    // Just close the modal and show success
    setCapturedImage(null);
    toast.success('✅ Selfie saved successfully!');
  };

  const retakePhoto = () => {
    setCapturedImage(null);
    setFormData(prev => ({ ...prev, liveness_photo: null }));
    setDocumentNames(prev => ({ ...prev, liveness_photo: '' }));
    startCamera();
  };

  // ============================================
  // ✅ PASSWORD VALIDATION
  // ============================================
  const validatePassword = (password) => {
    const errors = {
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecial: /[@$!%*?&]/.test(password)
    };
    setPasswordErrors(errors);

    const score = Object.values(errors).filter(Boolean).length;
    setPasswordStrength(score);
    
    return score === 5;
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, password: value }));
    validatePassword(value);
  };

  const getPasswordStrengthText = () => {
    if (passwordStrength === 0) return { text: 'No password', color: '#9CA3AF' };
    if (passwordStrength <= 2) return { text: 'Weak', color: '#EF4444' };
    if (passwordStrength <= 3) return { text: 'Medium', color: '#F59E0B' };
    if (passwordStrength <= 4) return { text: 'Strong', color: '#10B981' };
    return { text: 'Very Strong', color: '#059669' };
  };

  const getPasswordStrengthWidth = () => {
    return (passwordStrength / 5) * 100;
  };

  // ============================================
  // ✅ HANDLE CHANGE
  // ============================================
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e, field) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Please upload JPEG, PNG, or PDF files only');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      setFormData(prev => ({ ...prev, [field]: file }));
      setDocumentNames(prev => ({ ...prev, [field]: file.name }));
    }
  };

  // ============================================
  // ✅ FLEET FUNCTIONS
  // ============================================
  const handleTruckChange = (index, field, value) => {
    const updatedTrucks = [...formData.trucks];
    updatedTrucks[index] = { ...updatedTrucks[index], [field]: value };
    setFormData(prev => ({ ...prev, trucks: updatedTrucks }));
  };

  const addTruck = () => {
    setFormData(prev => ({
      ...prev,
      trucks: [...prev.trucks, { registration: '', make: '', model: '', year: '' }]
    }));
  };

  const removeTruck = (index) => {
    const updatedTrucks = formData.trucks.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, trucks: updatedTrucks }));
  };

  const handleTrailerChange = (index, field, value) => {
    const updatedTrailers = [...formData.trailers];
    updatedTrailers[index] = { ...updatedTrailers[index], [field]: value };
    setFormData(prev => ({ ...prev, trailers: updatedTrailers }));
  };

  const addTrailer = () => {
    setFormData(prev => ({
      ...prev,
      trailers: [...prev.trailers, { registration: '', type: '', capacity: '' }]
    }));
  };

  const removeTrailer = (index) => {
    const updatedTrailers = formData.trailers.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, trailers: updatedTrailers }));
  };

  const handleDriverChange = (index, field, value) => {
    const updatedDrivers = [...formData.drivers];
    updatedDrivers[index] = { ...updatedDrivers[index], [field]: value };
    setFormData(prev => ({ ...prev, drivers: updatedDrivers }));
  };

  const addDriver = () => {
    setFormData(prev => ({
      ...prev,
      drivers: [...prev.drivers, { name: '', id_number: '', prdp_number: '', prdp_expiry: '' }]
    }));
  };

  const removeDriver = (index) => {
    const updatedDrivers = formData.drivers.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, drivers: updatedDrivers }));
  };

  // ============================================
  // ✅ SUBMIT
  // ============================================
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate passwords match
      if (formData.password !== formData.confirm_password) {
        toast.error('Passwords do not match');
        setLoading(false);
        return;
      }

      // ✅ PASSWORD REQUIREMENTS VALIDATION
      const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
      if (!passwordRegex.test(formData.password)) {
        toast.error('Password must be at least 8 characters with 1 uppercase, 1 lowercase, 1 number, and 1 special character (@$!%*?&)');
        setLoading(false);
        return;
      }

      // ✅ CAPTCHA VALIDATION
      if (!captchaValid) {
        toast.error('Please complete the CAPTCHA verification');
        setLoading(false);
        return;
      }

      // ✅ TERMS ACCEPTANCE
      if (!formData.accept_terms) {
        toast.error('Please accept the Terms and Conditions');
        setLoading(false);
        return;
      }

      const submitData = new FormData();
      
      // Company Info
      submitData.append('company_name', formData.company_name);
      submitData.append('trading_name', formData.trading_name || '');
      submitData.append('registration_number', formData.registration_number);
      submitData.append('vat_number', formData.vat_number || '');
      submitData.append('company_address', formData.company_address);
      submitData.append('postal_address', formData.postal_address || '');
      submitData.append('company_phone', formData.company_phone);
      submitData.append('company_fax', formData.company_fax || '');
      submitData.append('company_website', formData.company_website || '');
      submitData.append('years_in_business', formData.years_in_business);
      submitData.append('operating_areas', formData.operating_areas);
      submitData.append('contact_person', formData.contact_person);
      submitData.append('contact_email', formData.contact_email);
      submitData.append('contact_phone', formData.contact_phone);
      submitData.append('position', formData.position || '');
      submitData.append('id_number', formData.id_number);
      submitData.append('password', formData.password);
      
      // ✅ Insurance Details
      submitData.append('git_insurance_provider', formData.git_insurance_provider || '');
      submitData.append('git_policy_number', formData.git_policy_number || '');
      submitData.append('git_insurance_expiry', formData.git_insurance_expiry || '');
      submitData.append('git_cover_amount', formData.git_cover_amount || '');
      submitData.append('public_liability_provider', formData.public_liability_provider || '');
      submitData.append('public_liability_policy_number', formData.public_liability_policy_number || '');
      submitData.append('public_liability_expiry', formData.public_liability_expiry || '');
      submitData.append('public_liability_cover_amount', formData.public_liability_cover_amount || '');
      submitData.append('tax_clearance_expiry', formData.tax_clearance_expiry || '');
      
      // Banking
      submitData.append('bank_name', formData.bank_name);
      submitData.append('bank_branch', formData.bank_branch || '');
      submitData.append('bank_account_number', formData.bank_account_number);
      submitData.append('bank_account_type', formData.bank_account_type);
      submitData.append('bank_branch_code', formData.bank_branch_code || '');
      
      // References
      submitData.append('reference_1_name', formData.reference_1_name);
      submitData.append('reference_1_company', formData.reference_1_company);
      submitData.append('reference_1_phone', formData.reference_1_phone);
      submitData.append('reference_1_email', formData.reference_1_email);
      submitData.append('reference_2_name', formData.reference_2_name);
      submitData.append('reference_2_company', formData.reference_2_company);
      submitData.append('reference_2_phone', formData.reference_2_phone);
      submitData.append('reference_2_email', formData.reference_2_email);
      
      // Fleet counts
      submitData.append('total_tractors', formData.total_tractors);
      submitData.append('total_trailers', formData.total_trailers);
      submitData.append('total_drivers', formData.total_drivers);
      
      // Fleet Capabilities
      submitData.append('has_own_workshop', formData.has_own_workshop);
      submitData.append('has_24hr_breakdown', formData.has_24hr_breakdown);
      submitData.append('has_tracking_system', formData.has_tracking_system);
      submitData.append('preferred_route', formData.preferred_route);
      
      // Documents
      const docFields = [
        'cipc_document', 'cipc_annual_return', 'shareholder_register',
        'git_insurance', 'public_liability', 'operator_card',
        'bank_confirmation', 'bank_statement', 'tax_clearance',
        'bbbee_certificate', 'id_document', 'liveness_photo'
      ];
      
      docFields.forEach(field => {
        if (formData[field]) {
          submitData.append(field, formData[field]);
        }
      });

      // Fleet details as JSON
      submitData.append('trucks', JSON.stringify(formData.trucks));
      submitData.append('trailers', JSON.stringify(formData.trailers));
      submitData.append('drivers', JSON.stringify(formData.drivers));

      console.log('📤 Submitting application for:', formData.company_name);

      const response = await fetch(`${API_URL}/subcontractors/register`, {
        method: 'POST',
        body: submitData
      });

      const data = await response.json();
      console.log('📥 Response:', data);

      if (data.success) {
        setSubmittedCompany(formData.company_name);
        setSubmittedEmail(formData.contact_email);
        setShowSuccess(true);
        toast.success('✅ Application submitted successfully!');
      } else {
        let errorMessage = data.error || 'Failed to submit application';
        
        if (errorMessage && errorMessage.includes('Email already registered')) {
          toast.error(
            <div style={{ padding: '4px 0' }}>
              <p style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '16px' }}>
                ❌ Email Already Registered
              </p>
              <p style={{ marginBottom: '8px', fontSize: '14px', color: '#4B5563' }}>
                This email is already registered in our system.
              </p>
              <div style={{ 
                backgroundColor: '#F3F4F6', 
                padding: '12px', 
                borderRadius: '8px',
                marginBottom: '8px'
              }}>
                <p style={{ fontSize: '13px', color: '#4B5563', marginBottom: '4px' }}>
                  <strong>Email:</strong> {formData.contact_email}
                </p>
                <p style={{ fontSize: '13px', color: '#4B5563' }}>
                  <strong>Company:</strong> {formData.company_name}
                </p>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '8px' }}>
                <a 
                  href="/login" 
                  style={{ 
                    color: '#00A86B', 
                    textDecoration: 'underline',
                    fontWeight: '500',
                    fontSize: '14px'
                  }}
                >
                  🔐 Login to your existing account
                </a>
                <a 
                  href="/forgot-password" 
                  style={{ 
                    color: '#F59E0B', 
                    textDecoration: 'underline',
                    fontWeight: '500',
                    fontSize: '14px'
                  }}
                >
                  🔑 Forgot your password? Reset it here
                </a>
                <p style={{ fontSize: '13px', color: '#6B7280', marginTop: '4px' }}>
                  Or use a different email address to sign up.
                </p>
              </div>
            </div>,
            { 
              autoClose: 15000,
              position: 'top-center'
            }
          );
        } else {
          toast.error(errorMessage);
        }
        console.error('❌ Error details:', data);
      }
    } catch (err) {
      console.error('Error submitting application:', err);
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    setStep(step + 1);
    window.scrollTo(0, 0);
  };

  const prevStep = () => {
    setStep(step - 1);
    window.scrollTo(0, 0);
  };

  const renderStepIndicator = () => {
    const steps = [
      { num: 1, label: 'Company' },
      { num: 2, label: 'Documents' },
      { num: 3, label: 'Identity' },
      { num: 4, label: 'Fleet' },
      { num: 5, label: 'Account' }
    ];

    return (
      <div className="flex justify-between mb-8">
        {steps.map(s => (
          <div key={s.num} className="flex flex-col items-center flex-1">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
              s.num < step ? 'bg-green-500 text-white' :
              s.num === step ? 'bg-navy text-white' :
              'bg-gray-200 text-gray-500'
            }`}>
              {s.num < step ? <FaCheckCircle /> : s.num}
            </div>
            <span className="text-xs mt-1 text-gray-500">{s.label}</span>
          </div>
        ))}
      </div>
    );
  };

  // ============================================
  // ✅ SUCCESS SCREEN
  // ============================================
  if (showSuccess) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4 flex items-center justify-center">
        <div className="max-w-2xl w-full bg-white rounded-2xl shadow-2xl p-8 text-center">
          <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <FaCheckCircle className="text-5xl text-green-500" />
          </div>
          <h1 className="text-3xl font-bold text-navy mb-2">
            ✅ Application Submitted!
          </h1>
          <p className="text-gray-600 text-lg mb-4">
            Thank you, <strong>{submittedCompany}</strong>!
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6 text-left">
            <h3 className="font-semibold text-navy mb-2 flex items-center gap-2">
              <FaEnvelopeIcon className="text-blue-600" /> What happens next?
            </h3>
            <ul className="space-y-3 text-sm text-gray-700">
              <li className="flex items-start gap-3">
                <span className="text-blue-500 font-bold">1.</span>
                <span><strong>Check your email</strong> - We've sent a verification link to <strong>{submittedEmail}</strong>. Please click the link to verify your email address.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-blue-500 font-bold">2.</span>
                <span><strong>Wait for admin approval</strong> - Our team will review your application within 24-48 hours.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-blue-500 font-bold">3.</span>
                <span><strong>Login once approved</strong> - You'll receive a notification when your account is activated.</span>
              </li>
            </ul>
          </div>
          
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 text-left">
            <p className="text-sm text-yellow-800 flex items-start gap-2">
              <FaInfoCircle className="mt-0.5 flex-shrink-0" />
              <span>
                <strong>Important:</strong> Your account is <strong>pending</strong> until verified. You will not be able to login until an admin approves your application.
              </span>
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/login"
              className="px-8 py-3 bg-green text-white rounded-lg font-medium hover:bg-green transition-colors flex items-center justify-center gap-2"
            >
              <FaCheckCircle /> Go to Login
            </Link>
            <Link
              to="/"
              className="px-8 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
            >
              <FaBuilding /> Return Home
            </Link>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-xs text-gray-400">
              If you don't receive the email within 5 minutes, check your spam folder or 
              <button 
                onClick={() => {
                  toast.info('Resending verification email...');
                }}
                className="text-green-600 hover:underline ml-1"
              >
                click here to resend
              </button>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-lg p-8">
        {/* Header - Updated with OneView Logo */}
        <div className="text-center mb-8">
          <div className="w-48 h-48 mx-auto mb-4">
            <img 
              src="/Images/OneViewLogo clear background.png" 
              alt="OneView" 
              className="w-full h-full object-contain"
            />
          </div>
          <h1 className="text-3xl font-bold" style={{ color: '#0A1929' }}>
            Become a <span style={{ color: '#00A86B' }}>One Purpose</span> Partner
          </h1>
          <p className="text-gray-500 mt-2">Join our network of trusted subcontractors</p>
        </div>

        {renderStepIndicator()}

        <form onSubmit={handleSubmit}>
          {/* STEP 1: COMPANY INFO - Unchanged */}
          {step === 1 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold" style={{ color: '#0A1929' }}>
                <FaBuilding className="inline mr-2" style={{ color: '#00A86B' }} />
                Company Information
              </h2>
              <p className="text-sm text-gray-500">Please provide your company registration details</p>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Trading Name (Optional)
                  </label>
                  <input
                    type="text"
                    name="trading_name"
                    value={formData.trading_name}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    CIPC Registration Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="registration_number"
                    value={formData.registration_number}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                    placeholder="e.g. 2024/123456/07"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    VAT Number (Optional)
                  </label>
                  <input
                    type="text"
                    name="vat_number"
                    value={formData.vat_number}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                    placeholder="e.g. 4123456789"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company Phone <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="company_phone"
                    value={formData.company_phone}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                    placeholder="+27 82 123 4567"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company Fax (Optional)
                  </label>
                  <input
                    type="tel"
                    name="company_fax"
                    value={formData.company_fax}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                    placeholder="+27 86 123 4567"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company Website (Optional)
                  </label>
                  <input
                    type="url"
                    name="company_website"
                    value={formData.company_website}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                    placeholder="https://www.example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Years in Business <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="years_in_business"
                    value={formData.years_in_business}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                    placeholder="e.g. 5"
                    min="0"
                    required
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Physical Address <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="company_address"
                    value={formData.company_address}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                    rows="2"
                    placeholder="Street address, city, province, postal code"
                    required
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Postal Address (Optional)
                  </label>
                  <textarea
                    name="postal_address"
                    value={formData.postal_address}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                    rows="2"
                    placeholder="Postal address if different from physical"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Preferred Operating Areas <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="operating_areas"
                    value={formData.operating_areas}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                    placeholder="e.g. Northern Cape, Limpopo, Gauteng, Cross-border (Zimbabwe, Zambia)"
                    required
                  />
                </div>
              </div>

              <div className="border-t pt-6 mt-6">
                <h3 className="text-lg font-semibold" style={{ color: '#0A1929' }}>
                  <FaUserTie className="inline mr-2" style={{ color: '#00A86B' }} />
                  Contact Person
                </h3>
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="contact_person"
                      value={formData.contact_person}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Position/Role <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="position"
                      value={formData.position}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                      placeholder="e.g. Director, Owner, Manager"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="contact_email"
                      value={formData.contact_email}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="contact_phone"
                      value={formData.contact_phone}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                      placeholder="+27 82 123 4567"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={nextStep}
                  className="px-6 py-2 rounded-lg text-white font-medium flex items-center gap-2"
                  style={{ backgroundColor: '#00A86B' }}
                >
                  Next Step <FaArrowRight />
                </button>
              </div>
            </div>
          )}

          {/* STEP 2: DOCUMENTS & INSURANCE - Unchanged */}
          {step === 2 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold" style={{ color: '#0A1929' }}>
                <FaFileAlt className="inline mr-2" style={{ color: '#00A86B' }} />
                Documents & Insurance
              </h2>
              <p className="text-sm text-gray-500">Upload required documents and provide insurance details</p>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-yellow-800 flex items-start gap-2">
                  <FaInfoCircle className="mt-0.5 flex-shrink-0" />
                  <span>
                    <strong>Required Documents:</strong> All documents must be clear, legible, and in PDF, JPEG, or PNG format (max 10MB per file).
                  </span>
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    CIPC Registration Certificate <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileChange(e, 'cipc_document')}
                    className="w-full text-sm"
                  />
                  {documentNames.cipc_document && (
                    <span className="text-xs text-green-600">✅ {documentNames.cipc_document}</span>
                  )}
                </div>

                <div className="border rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    CIPC Annual Return <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileChange(e, 'cipc_annual_return')}
                    className="w-full text-sm"
                  />
                  {documentNames.cipc_annual_return && (
                    <span className="text-xs text-green-600">✅ {documentNames.cipc_annual_return}</span>
                  )}
                </div>

                <div className="border rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Shareholder/Director Register <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileChange(e, 'shareholder_register')}
                    className="w-full text-sm"
                  />
                  {documentNames.shareholder_register && (
                    <span className="text-xs text-green-600">✅ {documentNames.shareholder_register}</span>
                  )}
                </div>

                <div className="border rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tax Clearance Certificate <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileChange(e, 'tax_clearance')}
                    className="w-full text-sm"
                  />
                  {documentNames.tax_clearance && (
                    <span className="text-xs text-green-600">✅ {documentNames.tax_clearance}</span>
                  )}
                  <div className="mt-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tax Clearance Expiry Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      name="tax_clearance_expiry"
                      value={formData.tax_clearance_expiry || ''}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                    />
                  </div>
                </div>

                <div className="border rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    GIT Insurance Certificate <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileChange(e, 'git_insurance')}
                    className="w-full text-sm"
                  />
                  {documentNames.git_insurance && (
                    <span className="text-xs text-green-600">✅ {documentNames.git_insurance}</span>
                  )}
                </div>

                <div className="border rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Public Liability Insurance <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileChange(e, 'public_liability')}
                    className="w-full text-sm"
                  />
                  {documentNames.public_liability && (
                    <span className="text-xs text-green-600">✅ {documentNames.public_liability}</span>
                  )}
                </div>

                <div className="border rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Operator Card (GIT) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileChange(e, 'operator_card')}
                    className="w-full text-sm"
                  />
                  {documentNames.operator_card && (
                    <span className="text-xs text-green-600">✅ {documentNames.operator_card}</span>
                  )}
                </div>

                <div className="border rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bank Confirmation Letter <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileChange(e, 'bank_confirmation')}
                    className="w-full text-sm"
                  />
                  {documentNames.bank_confirmation && (
                    <span className="text-xs text-green-600">✅ {documentNames.bank_confirmation}</span>
                  )}
                </div>

                <div className="border rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bank Statement / Proof of Account <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileChange(e, 'bank_statement')}
                    className="w-full text-sm"
                  />
                  {documentNames.bank_statement && (
                    <span className="text-xs text-green-600">✅ {documentNames.bank_statement}</span>
                  )}
                </div>

                <div className="border rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    B-BBEE Certificate (Optional)
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileChange(e, 'bbbee_certificate')}
                    className="w-full text-sm"
                  />
                  {documentNames.bbbee_certificate && (
                    <span className="text-xs text-green-600">✅ {documentNames.bbbee_certificate}</span>
                  )}
                </div>
              </div>

              {/* ✅ INSURANCE DETAILS */}
              <div className="border-t pt-6 mt-6">
                <h3 className="text-lg font-semibold" style={{ color: '#0A1929' }}>
                  <FaShieldAlt className="inline mr-2" style={{ color: '#00A86B' }} />
                  Insurance Details
                </h3>
                <p className="text-sm text-gray-500 mb-4">Provide your insurance details for verification</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* GIT Insurance */}
                  <div className="border rounded-lg p-4 bg-gray-50">
                    <h4 className="font-medium text-navy mb-3">GIT Insurance (Goods in Transit)</h4>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Insurance Provider <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="git_insurance_provider"
                          value={formData.git_insurance_provider || ''}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                          placeholder="e.g. Old Mutual, Santam"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Policy Number <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="git_policy_number"
                          value={formData.git_policy_number || ''}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                          placeholder="e.g. GIT-2026-001"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Expiry Date <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="date"
                          name="git_insurance_expiry"
                          value={formData.git_insurance_expiry || ''}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Cover Amount (R) <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          name="git_cover_amount"
                          value={formData.git_cover_amount || ''}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                          placeholder="e.g. 2000000"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Public Liability */}
                  <div className="border rounded-lg p-4 bg-gray-50">
                    <h4 className="font-medium text-navy mb-3">Public Liability Insurance</h4>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Insurance Provider <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="public_liability_provider"
                          value={formData.public_liability_provider || ''}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                          placeholder="e.g. Old Mutual, Santam"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Policy Number <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="public_liability_policy_number"
                          value={formData.public_liability_policy_number || ''}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                          placeholder="e.g. PL-2026-001"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Expiry Date <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="date"
                          name="public_liability_expiry"
                          value={formData.public_liability_expiry || ''}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Cover Amount (R) <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          name="public_liability_cover_amount"
                          value={formData.public_liability_cover_amount || ''}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                          placeholder="e.g. 5000000"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Banking Details */}
              <div className="border-t pt-6 mt-6">
                <h3 className="text-lg font-semibold" style={{ color: '#0A1929' }}>
                  <FaUniversity className="inline mr-2" style={{ color: '#00A86B' }} />
                  Banking Details
                </h3>
                <p className="text-sm text-gray-500 mb-4">Provide your business banking details for payments</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Bank Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="bank_name"
                      value={formData.bank_name}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                      placeholder="e.g. Standard Bank"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Branch Name
                    </label>
                    <input
                      type="text"
                      name="bank_branch"
                      value={formData.bank_branch}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                      placeholder="e.g. Sandton City"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Bank Account Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="bank_account_number"
                      value={formData.bank_account_number}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                      placeholder="e.g. 1234567890"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Branch Code
                    </label>
                    <input
                      type="text"
                      name="bank_branch_code"
                      value={formData.bank_branch_code}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                      placeholder="e.g. 051001"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Account Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="bank_account_type"
                      value={formData.bank_account_type}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                      required
                    >
                      <option value="business">Business</option>
                      <option value="current">Current</option>
                      <option value="cheque">Cheque</option>
                      <option value="savings">Savings</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* References */}
              <div className="border-t pt-6 mt-6">
                <h3 className="text-lg font-semibold" style={{ color: '#0A1929' }}>
                  <FaUsers className="inline mr-2" style={{ color: '#00A86B' }} />
                  References
                </h3>
                <p className="text-sm text-gray-500 mb-4">Provide two professional references</p>
                
                {/* Reference 1 */}
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <h4 className="font-medium text-navy mb-3">Reference 1</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="reference_1_name"
                        value={formData.reference_1_name}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                        placeholder="e.g. John Doe"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Company <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="reference_1_company"
                        value={formData.reference_1_company}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                        placeholder="e.g. ABC Mining"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        name="reference_1_phone"
                        value={formData.reference_1_phone}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                        placeholder="+27 82 123 4567"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="reference_1_email"
                        value={formData.reference_1_email}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                        placeholder="john@abcmining.co.za"
                        required
                      />
                    </div>
                  </div>
                </div>
                
                {/* Reference 2 */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-navy mb-3">Reference 2</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="reference_2_name"
                        value={formData.reference_2_name}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                        placeholder="e.g. Jane Smith"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Company <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="reference_2_company"
                        value={formData.reference_2_company}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                        placeholder="e.g. XYZ Logistics"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        name="reference_2_phone"
                        value={formData.reference_2_phone}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                        placeholder="+27 82 123 4567"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="reference_2_email"
                        value={formData.reference_2_email}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                        placeholder="jane@xyzlogistics.co.za"
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-between">
                <button
                  type="button"
                  onClick={prevStep}
                  className="px-6 py-2 border border-gray-300 rounded-lg font-medium hover:bg-gray-50"
                >
                  Previous
                </button>
                <button
                  type="button"
                  onClick={nextStep}
                  className="px-6 py-2 rounded-lg text-white font-medium flex items-center gap-2"
                  style={{ backgroundColor: '#00A86B' }}
                >
                  Next Step <FaArrowRight />
                </button>
              </div>
            </div>
          )}

          {/* STEP 3: IDENTITY & LIVENESS - FIXED */}
          {step === 3 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold" style={{ color: '#0A1929' }}>
                <FaIdCard className="inline mr-2" style={{ color: '#00A86B' }} />
                Identity Verification
              </h2>
              <p className="text-sm text-gray-500">Verify your identity with ID document and liveness photo</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ID Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="id_number"
                    value={formData.id_number}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                    placeholder="e.g. 8001015009087"
                    required
                  />
                </div>

                <div className="border rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ID Document Copy <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileChange(e, 'id_document')}
                    className="w-full text-sm"
                  />
                  {documentNames.id_document && (
                    <span className="text-xs text-green-600">✅ {documentNames.id_document}</span>
                  )}
                  <p className="text-xs text-gray-400 mt-1">Upload a clear copy of your ID/passport</p>
                </div>

                {/* Liveness Photo */}
                <div className="border rounded-lg p-4 col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Liveness Photo (Selfie) <span className="text-red-500">*</span>
                  </label>
                  
                  <div className="flex items-center gap-4">
                    <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 overflow-hidden">
                      {capturedImage ? (
                        <img src={capturedImage} alt="Selfie" className="w-full h-full object-cover" />
                      ) : formData.liveness_photo ? (
                        <div className="text-center">
                          <FaCheckCircle className="text-3xl text-green-500 mx-auto" />
                          <span className="text-xs text-gray-500 mt-1 block">Photo saved</span>
                        </div>
                      ) : (
                        <div className="text-center">
                          <FaCamera className="text-3xl text-gray-400 mx-auto" />
                          <span className="text-xs text-gray-500 mt-1 block">Take a selfie</span>
                        </div>
                      )}
                    </div>

                    <div className="flex-1">
                      <button
                        type="button"
                        onClick={startCamera}
                        style={{
                          padding: '8px 20px',
                          backgroundColor: '#3B82F6',
                          color: 'white',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: '500',
                          border: 'none',
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2563EB'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#3B82F6'}
                      >
                        <FaVideo /> Take Selfie (Camera)
                      </button>
                      <p className="text-xs text-gray-500 mt-2">or</p>
                      <input
                        type="file"
                        accept=".jpg,.jpeg,.png"
                        onChange={(e) => handleFileChange(e, 'liveness_photo')}
                        className="w-full text-sm mt-2"
                      />
                      {documentNames.liveness_photo && !capturedImage && (
                        <span className="text-xs text-green-600">✅ {documentNames.liveness_photo}</span>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        Take a clear selfie holding your ID document next to your face
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-between">
                <button
                  type="button"
                  onClick={prevStep}
                  className="px-6 py-2 border border-gray-300 rounded-lg font-medium hover:bg-gray-50"
                >
                  Previous
                </button>
                <button
                  type="button"
                  onClick={nextStep}
                  className="px-6 py-2 rounded-lg text-white font-medium flex items-center gap-2"
                  style={{ backgroundColor: '#00A86B' }}
                >
                  Next Step <FaArrowRight />
                </button>
              </div>
            </div>
          )}

          {/* CAMERA MODAL - FIXED */}
          {showCamera && (
            <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 backdrop-blur-sm">
              <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden">
                <div className="bg-[#0A1929] px-6 py-4 flex justify-between items-center">
                  <h3 className="text-white font-semibold flex items-center gap-2">
                    <FaCamera className="text-green-400" /> Take Selfie
                  </h3>
                  <button
                    type="button"
                    onClick={stopCamera}
                    className="text-white/80 hover:text-white transition-colors"
                  >
                    <FaTimes size={24} />
                  </button>
                </div>

                <div className="relative bg-black" style={{ minHeight: '400px' }}>
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-full h-auto min-h-[400px] object-cover"
                    style={{ 
                      backgroundColor: '#000',
                      display: 'block',
                      width: '100%',
                      height: 'auto',
                      minHeight: '400px',
                      maxHeight: '500px'
                    }}
                  />
                  <canvas ref={canvasRef} className="hidden" />
                  
                  <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute inset-x-4 top-1/2 -translate-y-1/2 h-48 border-2 border-white/30 rounded-lg"></div>
                    <div className="absolute bottom-4 left-0 right-0 text-center text-white/50 text-xs">
                      📸 Position your face inside the box and click Capture
                    </div>
                  </div>
                  
                  {!cameraStream && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/80">
                      <div className="text-white text-center">
                        <FaSpinner className="animate-spin text-4xl mx-auto mb-2" />
                        <p className="text-sm">Starting camera...</p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="bg-gray-50 px-6 py-4 flex justify-center gap-4">
                  <button
                    type="button"
                    onClick={capturePhoto}
                    disabled={!cameraStream}
                    style={{
                      padding: '10px 24px',
                      backgroundColor: '#3B82F6',
                      color: 'white',
                      borderRadius: '9999px',
                      fontSize: '14px',
                      fontWeight: '500',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      opacity: !cameraStream ? 0.5 : 1
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2563EB'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#3B82F6'}
                  >
                    <FaCamera /> Capture Photo
                  </button>
                  <button
                    type="button"
                    onClick={stopCamera}
                    style={{
                      padding: '10px 24px',
                      backgroundColor: '#9CA3AF',
                      color: 'white',
                      borderRadius: '9999px',
                      fontSize: '14px',
                      fontWeight: '500',
                      border: 'none',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#6B7280'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#9CA3AF'}
                  >
                    Cancel
                  </button>
                </div>

                <div className="bg-blue-50 px-6 py-3 text-xs text-blue-700 text-center">
                  🔒 Camera access is required. Please allow camera permissions when prompted.
                </div>
              </div>
            </div>
          )}

          {/* CAPTURED PREVIEW MODAL - FIXED */}
          {capturedImage && !showCamera && (
            <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm">
              <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
                <div className="bg-green-500 px-6 py-4 flex justify-between items-center">
                  <h3 className="text-white font-semibold flex items-center gap-2" style={{ color: '#0A1929' }}>
                    <FaCheckCircle style={{ color: '#0A1929' }} /> Selfie Captured
                  </h3>
                  <button
                    type="button"
                    onClick={() => {
                      setCapturedImage(null);
                      setFormData(prev => ({ ...prev, liveness_photo: null }));
                      setDocumentNames(prev => ({ ...prev, liveness_photo: '' }));
                    }}
                    className="text-white/80 hover:text-white transition-colors"
                  >
                    <FaTimes size={24} />
                  </button>
                </div>
                
                <div className="p-6">
                  <img src={capturedImage} alt="Captured selfie" className="w-full rounded-lg" />
                </div>

                <div className="bg-gray-50 px-6 py-4 flex justify-center gap-4">
                  <button
                    type="button"
                    onClick={retakePhoto}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#F59E0B',
                      color: 'white',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#D97706'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#F59E0B'}
                  >
                    <FaRedo /> Retake Photo
                  </button>
                  <button
                    type="button"
                    onClick={useCapturedPhoto}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#00A86B',
                      color: 'white',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#00995E'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#00A86B'}
                  >
                    <FaCheckCircle /> Use This Photo
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* STEP 4: FLEET DETAILS - Unchanged */}
          {step === 4 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold" style={{ color: '#0A1929' }}>
                <FaTruck className="inline mr-2" style={{ color: '#00A86B' }} />
                Fleet Details
              </h2>
              <p className="text-sm text-gray-500">Provide details of your fleet</p>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Total Tractors <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="total_tractors"
                    value={formData.total_tractors}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                    min="0"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Total Trailers <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="total_trailers"
                    value={formData.total_trailers}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                    min="0"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Total Drivers <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="total_drivers"
                    value={formData.total_drivers}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                    min="0"
                    required
                  />
                </div>
              </div>

              <div className="border rounded-lg p-4">
                <h3 className="font-semibold text-navy mb-3">Fleet Capabilities</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Own Workshop? <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="has_own_workshop"
                      value={formData.has_own_workshop}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                      required
                    >
                      <option value="false">No</option>
                      <option value="true">Yes</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      24hr Breakdown Service? <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="has_24hr_breakdown"
                      value={formData.has_24hr_breakdown}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                      required
                    >
                      <option value="false">No</option>
                      <option value="true">Yes</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      GPS Tracking System? <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="has_tracking_system"
                      value={formData.has_tracking_system}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                      required
                    >
                      <option value="false">No</option>
                      <option value="true">Yes</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Preferred Route Type
                    </label>
                    <select
                      name="preferred_route"
                      value={formData.preferred_route}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                    >
                      <option value="">Select...</option>
                      <option value="domestic">Domestic Only</option>
                      <option value="cross_border">Cross-Border Only</option>
                      <option value="both">Both Domestic & Cross-Border</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Trucks */}
              <div className="border rounded-lg p-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold text-navy">Trucks/Tractors</h3>
                  <button
                    type="button"
                    onClick={addTruck}
                    className="px-3 py-1 bg-blue-500 text-white rounded-lg text-sm flex items-center gap-1"
                  >
                    <FaPlus size={12} /> Add Truck
                  </button>
                </div>

                {formData.trucks.map((truck, index) => (
                  <div key={index} className="grid grid-cols-5 gap-2 mb-2 p-2 bg-gray-50 rounded-lg">
                    <input
                      type="text"
                      placeholder="Reg Number"
                      value={truck.registration || ''}
                      onChange={(e) => handleTruckChange(index, 'registration', e.target.value)}
                      className="px-2 py-1 border border-gray-300 rounded-lg text-sm"
                    />
                    <input
                      type="text"
                      placeholder="Make"
                      value={truck.make || ''}
                      onChange={(e) => handleTruckChange(index, 'make', e.target.value)}
                      className="px-2 py-1 border border-gray-300 rounded-lg text-sm"
                    />
                    <input
                      type="text"
                      placeholder="Model"
                      value={truck.model || ''}
                      onChange={(e) => handleTruckChange(index, 'model', e.target.value)}
                      className="px-2 py-1 border border-gray-300 rounded-lg text-sm"
                    />
                    <input
                      type="text"
                      placeholder="Year"
                      value={truck.year || ''}
                      onChange={(e) => handleTruckChange(index, 'year', e.target.value)}
                      className="px-2 py-1 border border-gray-300 rounded-lg text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => removeTruck(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <FaTrash />
                    </button>
                  </div>
                ))}
              </div>

              {/* Trailers */}
              <div className="border rounded-lg p-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold text-navy">Trailers</h3>
                  <button
                    type="button"
                    onClick={addTrailer}
                    className="px-3 py-1 bg-blue-500 text-white rounded-lg text-sm flex items-center gap-1"
                  >
                    <FaPlus size={12} /> Add Trailer
                  </button>
                </div>

                {formData.trailers.map((trailer, index) => (
                  <div key={index} className="grid grid-cols-4 gap-2 mb-2 p-2 bg-gray-50 rounded-lg">
                    <input
                      type="text"
                      placeholder="Reg Number"
                      value={trailer.registration || ''}
                      onChange={(e) => handleTrailerChange(index, 'registration', e.target.value)}
                      className="px-2 py-1 border border-gray-300 rounded-lg text-sm"
                    />
                    <input
                      type="text"
                      placeholder="Type (e.g. Side Tipper)"
                      value={trailer.type || ''}
                      onChange={(e) => handleTrailerChange(index, 'type', e.target.value)}
                      className="px-2 py-1 border border-gray-300 rounded-lg text-sm"
                    />
                    <input
                      type="text"
                      placeholder="Capacity (MT)"
                      value={trailer.capacity || ''}
                      onChange={(e) => handleTrailerChange(index, 'capacity', e.target.value)}
                      className="px-2 py-1 border border-gray-300 rounded-lg text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => removeTrailer(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <FaTrash />
                    </button>
                  </div>
                ))}
              </div>

              {/* Drivers with PRDP Expiry */}
              <div className="border rounded-lg p-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold text-navy">Drivers</h3>
                  <button
                    type="button"
                    onClick={addDriver}
                    className="px-3 py-1 bg-blue-500 text-white rounded-lg text-sm flex items-center gap-1"
                  >
                    <FaPlus size={12} /> Add Driver
                  </button>
                </div>

                {formData.drivers.map((driver, index) => (
                  <div key={index} className="grid grid-cols-5 gap-2 mb-2 p-2 bg-gray-50 rounded-lg">
                    <input
                      type="text"
                      placeholder="Full Name"
                      value={driver.name || ''}
                      onChange={(e) => handleDriverChange(index, 'name', e.target.value)}
                      className="px-2 py-1 border border-gray-300 rounded-lg text-sm"
                    />
                    <input
                      type="text"
                      placeholder="ID Number"
                      value={driver.id_number || ''}
                      onChange={(e) => handleDriverChange(index, 'id_number', e.target.value)}
                      className="px-2 py-1 border border-gray-300 rounded-lg text-sm"
                    />
                    <input
                      type="text"
                      placeholder="PrDP Number"
                      value={driver.prdp_number || ''}
                      onChange={(e) => handleDriverChange(index, 'prdp_number', e.target.value)}
                      className="px-2 py-1 border border-gray-300 rounded-lg text-sm"
                    />
                    <input
                      type="date"
                      placeholder="PrDP Expiry"
                      value={driver.prdp_expiry || ''}
                      onChange={(e) => handleDriverChange(index, 'prdp_expiry', e.target.value)}
                      className="px-2 py-1 border border-gray-300 rounded-lg text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => removeDriver(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <FaTrash />
                    </button>
                  </div>
                ))}
              </div>

              <div className="flex justify-between">
                <button
                  type="button"
                  onClick={prevStep}
                  className="px-6 py-2 border border-gray-300 rounded-lg font-medium hover:bg-gray-50"
                >
                  Previous
                </button>
                <button
                  type="button"
                  onClick={nextStep}
                  className="px-6 py-2 rounded-lg text-white font-medium flex items-center gap-2"
                  style={{ backgroundColor: '#00A86B' }}
                >
                  Next Step <FaArrowRight />
                </button>
              </div>
            </div>
          )}

          {/* STEP 5: ACCOUNT & SUBMIT - Unchanged */}
          {step === 5 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold" style={{ color: '#0A1929' }}>
                <FaLock className="inline mr-2" style={{ color: '#00A86B' }} />
                Create Account & Submit
              </h2>
              <p className="text-sm text-gray-500">Set up your account password and submit your application</p>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-800 flex items-start gap-2">
                  <FaInfoCircle className="mt-0.5 flex-shrink-0" />
                  <span>
                    <strong>Note:</strong> Your account will be created but <strong>inactive</strong> until your application is reviewed and approved by our team. You will receive a notification once your application is approved.
                  </span>
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handlePasswordChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-green ${
                        formData.password && passwordStrength === 5 ? 'border-green-500 bg-green-50' : 'border-gray-300'
                      }`}
                      minLength="8"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                  
                  <div className="mt-3 space-y-1.5">
                    <p className="text-xs font-medium text-gray-600">Password Requirements:</p>
                    <div className={`flex items-center gap-2 text-xs ${passwordErrors.minLength ? 'text-green-600' : 'text-gray-400'}`}>
                      {passwordErrors.minLength ? <FaCheckCircle className="text-green-500" /> : <FaTimes className="text-gray-300" />}
                      At least 8 characters
                    </div>
                    <div className={`flex items-center gap-2 text-xs ${passwordErrors.hasUppercase ? 'text-green-600' : 'text-gray-400'}`}>
                      {passwordErrors.hasUppercase ? <FaCheckCircle className="text-green-500" /> : <FaTimes className="text-gray-300" />}
                      At least 1 uppercase letter (A-Z)
                    </div>
                    <div className={`flex items-center gap-2 text-xs ${passwordErrors.hasLowercase ? 'text-green-600' : 'text-gray-400'}`}>
                      {passwordErrors.hasLowercase ? <FaCheckCircle className="text-green-500" /> : <FaTimes className="text-gray-300" />}
                      At least 1 lowercase letter (a-z)
                    </div>
                    <div className={`flex items-center gap-2 text-xs ${passwordErrors.hasNumber ? 'text-green-600' : 'text-gray-400'}`}>
                      {passwordErrors.hasNumber ? <FaCheckCircle className="text-green-500" /> : <FaTimes className="text-gray-300" />}
                      At least 1 number (0-9)
                    </div>
                    <div className={`flex items-center gap-2 text-xs ${passwordErrors.hasSpecial ? 'text-green-600' : 'text-gray-400'}`}>
                      {passwordErrors.hasSpecial ? <FaCheckCircle className="text-green-500" /> : <FaTimes className="text-gray-300" />}
                      At least 1 special character (@$!%*?&)
                    </div>
                  </div>

                  {formData.password && (
                    <div className="mt-3">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-xs font-medium text-gray-600">Password Strength</span>
                        <span className="text-xs font-bold" style={{ color: getPasswordStrengthText().color }}>
                          {getPasswordStrengthText().text}
                        </span>
                      </div>
                      <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-300"
                          style={{
                            width: `${getPasswordStrengthWidth()}%`,
                            backgroundColor: getPasswordStrengthText().color
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm Password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      name="confirm_password"
                      value={formData.confirm_password}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-green ${
                        formData.confirm_password && formData.password === formData.confirm_password && formData.password
                          ? 'border-green-500 bg-green-50'
                          : formData.confirm_password && formData.password !== formData.confirm_password
                          ? 'border-red-500 bg-red-50'
                          : 'border-gray-300'
                      }`}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                  {formData.confirm_password && formData.password !== formData.confirm_password && (
                    <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                      <FaTimes /> Passwords do not match
                    </p>
                  )}
                  {formData.confirm_password && formData.password === formData.confirm_password && formData.password && (
                    <p className="text-xs text-green-500 mt-1 flex items-center gap-1">
                      <FaCheckCircle /> Passwords match
                    </p>
                  )}
                </div>
              </div>

              <div className="mb-4">
                <SimpleCaptcha 
                  onValidate={() => setCaptchaValid(true)}
                  onChange={() => setCaptchaValid(false)}
                />
              </div>

              <div className="border-t pt-6 mt-6">
                <div className="flex items-start gap-3 mb-4">
                  <input
                    type="checkbox"
                    name="accept_terms"
                    checked={formData.accept_terms}
                    onChange={handleChange}
                    className="mt-1 w-5 h-5 rounded border-gray-300 text-green-600 focus:ring-green-500"
                    required
                  />
                  <label className="text-sm text-gray-700">
                    I have read and agree to the{' '}
                    <button
                      type="button"
                      onClick={() => setShowTermsModal(true)}
                      className="text-green-600 hover:underline font-medium"
                    >
                      Terms and Conditions
                    </button>
                    {' '}of One Purpose Logistics (Pty) Ltd.
                    <span className="text-red-500">*</span>
                  </label>
                </div>
              </div>

              {/* TERMS MODAL */}
              {showTermsModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                  <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowTermsModal(false)}></div>
                  <div className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                    <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl flex justify-between items-center">
                      <h2 className="text-xl font-bold text-navy">Terms and Conditions</h2>
                      <button onClick={() => setShowTermsModal(false)} className="text-gray-500 hover:text-navy">
                        <FaTimes size={24} />
                      </button>
                    </div>
                    <div className="p-6 space-y-4 text-sm text-gray-700">
                      <h3 className="text-lg font-bold text-navy">1. HEAD NOTES</h3>
                      <p>The head notes to the clauses herein are inserted for reference purposes only and shall in no way govern or affect the interpretation hereof.</p>

                      <h3 className="text-lg font-bold text-navy">2. TERMS AND CONDITIONS</h3>
                      <p>All carriage by the Sub-Contractor is and shall be subject to the terms and conditions as set out hereinafter and each condition shall be deemed to be incorporated in and to be a condition of any contract between One Purpose Logistics (Pty) Ltd and the Sub-Contractor and shall be binding for all future carriage unless these terms and conditions are altered by express written agreement or by change of the stated Conditions of Carriage prior to carriage.</p>
                      <p>In any conflict between these General Trading Conditions and the terms and conditions of any of One Purpose Logistics (Pty) Ltd.'s documents, the former shall prevail. These conditions shall be in place of any other conditions which may have previously been notified by One Purpose Logistics (Pty) Ltd to the Sub-Contractor.</p>

                      <h3 className="text-lg font-bold text-navy">3. TERM AND DURATION OF AGREEMENT</h3>
                      <p>The term and duration of this Agreement shall be effective as of the date of acceptance and shall be in force for a period of 1 year.</p>
                      <p>The Agreement will automatically be extended after the 1 year period and terminated by a party with at least (1) calendar months' prior written notice to the either party.</p>
                      <p>Notwithstanding above, this Agreement may be terminated at any time in any of the following ways:</p>
                      <ul className="list-disc pl-6 space-y-1">
                        <li>on at least 30 days' notice by Client to the Sub-Contractor, and vice versa;</li>
                        <li>failure by either Party to remedy a material breach of this Agreement which has not been remedied within 7 days after notice of the breach has been served by the other party;</li>
                        <li>Immediately by either Party if the other Party enters into any form of insolvency, bankruptcy, receivership, administration, or ceases or threatens to cease to carry on its business, or passes a resolution for winding up, or is unable to pay its debts.</li>
                      </ul>

                      <h3 className="text-lg font-bold text-navy">4. DEDICATED SUB-CONTRACTOR</h3>
                      <p>Will undergo an induction process whereby their vehicles will be inspected, drivers trained and medically certified on a bi-annually period to ensure that the highest levels of competent and fit vehicles and personal are in circulation.</p>
                      <p>Upon termination of this Agreement the Sub-Contractor will arrange for an exit induction.</p>

                      <h3 className="text-lg font-bold text-navy">5. APPOINTMENT AS SUB-CONTRACTOR</h3>
                      <p>a) Whereas One Purpose Logistics (Pty) Ltd acts as an agent for various principals in relation to the conveying of goods and is accordingly from time to time required to appoint contractors to attend to the conveying of the goods, One Purpose Logistics (Pty) Ltd hereby appoints the SUB-CONTRACTOR to transport such goods from time to time from such location to such other location as the parties may agree, and upon payment of such agreed fees.</p>
                      <p>b) The SUB-CONTRACTOR shall at all times act as an independent contractor, and this agreement shall not be construed or interpreted as to create any employment relationship between One Purpose Logistics (Pty) Ltd and/or its principal on the one side and the SUB-CONTRACTOR on the other side.</p>
                      <p>c) It is further recorded that One Purpose Logistics (Pty) Ltd do not allow the appointment of Sub-contractors to fulfil the obligations in terms of this agreement.</p>
                      <p>d) It is agreed and hereby recorded that One Purpose Logistics (Pty) Ltd shall be under no fixed obligation to commission the SUB-CONTRACTOR for the conveyance of the goods and shall in its sole and absolute discretion be entitled to elect to utilise the services of the SUB-CONTRACTOR for any particular load of goods. It is specifically agreed and hereby recorded that One Purpose Logistics (Pty) Ltd does not undertake nor does it warrant that it shall commission the SUB-CONTRACTOR for any specific number of loads of goods during the currency of this agreement.</p>

                      <h3 className="text-lg font-bold text-navy">6. B-BBEE CERTIFICATES</h3>
                      <p>It is the Sub-Contractor's responsibility to ensure that a valid B-BBEE certificate is sent to info@onepurposelogistics.com annually when the previous certificate expires.</p>
                      <p>If the supplier does not have a B-BBEE certificate and their turnover does not exceed R10 Million per annum, then an EME Affidavit Letter from the auditors/external accountants confirming the turnover and supporting documentation must be sent to info@onepurposelogistics.com.</p>

                      <h3 className="text-lg font-bold text-navy">7. TRANSPORT ORDERS</h3>
                      <p>One Purpose Logistics (Pty) Ltd shall furnish the Sub-Contractor with a Purchase Order in respect of each and every load to be transported by the Sub-Contractor.</p>
                      <p>The Purchase Order shall contain, inter alia, the order number, loading date, loading address, contact person, commodity, volume/weight/pallets, tariff, load value and authorised signature of One Purpose Logistics (Pty) Ltd.</p>
                      <p>The Sub-Contractor shall fully comply with the instruction contained in the Purchase Order.</p>
                      <p>All invoices submitted by the Sub-Contractor for payment, shall be accompanied by a copy of the Purchase Order and proof of delivery (POD). The Invoice needs to reflect the One Purpose Logistics (Pty) Ltd Purchase Order number.</p>
                      <p>One Purpose Logistics (Pty) Ltd shall not be obliged to settle any invoices unless the Purchase Order and proof of delivery is annexed thereto.</p>

                      <h3 className="text-lg font-bold text-navy">8. TARIFF</h3>
                      <p>The tariff payable by One Purpose Logistics (Pty) Ltd to the Sub-Contractor shall be the tariff as set out on the Purchase Order. No variation to the tariff shall be of any force or effect, unless recorded and signed by One Purpose Logistics (Pty) Ltd on the Purchase Order.</p>
                      <p>Any extra charges that need to be added requires an additional Purchase Order.</p>

                      <h3 className="text-lg font-bold text-navy">9. INVOICES, PROOF OF DELIVERIES AND TRANSPORT ORDERS</h3>
                      <p>All invoices submitted must be accompanied by a signed and stamped original proof of deliveries together with a copy of the Purchase Order, which serves as the payment confirmation for every load completed.</p>
                      <p>No copies of invoices or proof of deliveries will be accepted for payment purposes.</p>
                      <p>Following POD cut off times are to be adhered to:</p>
                      <ul className="list-disc pl-6 space-y-1">
                        <li>Scanned copies of PODs within 72 hours</li>
                        <li>Originals (hard copies) within 5 working days</li>
                        <li>Signed Delivery Note</li>
                        <li>Signed POD</li>
                        <li>Loading Weighbridge Slip/Ticket</li>
                        <li>Offloading Weighbridge Slip/Ticket</li>
                      </ul>

                      <h3 className="text-lg font-bold text-navy">10. OBLIGATIONS OF THE SUB-CONTRACTOR</h3>
                      <p>The SUB-CONTRACTOR shall take delivery of the goods at such addresses and at such times as it may be notified of in the Load Confirmation by One Purpose Logistics (Pty) Ltd.</p>
                      <p>The SUB-CONTRACTOR shall satisfy himself that all goods are suitable for carriage and that all goods are properly packed/loaded and/or crated to ensure correct loading and safe carriage. It is agreed that should the goods not be in a clean, dry and stable condition and/or if the goods reflect visible damage or contamination, then the SUB-CONTRACTOR shall not load the goods and in which event the SUB-CONTRACTOR shall immediately advise One Purpose Logistics (Pty) Ltd.</p>
                      <p>The SUB-CONTRACTOR shall provide the correct tonnage/lashing equipment to secure the goods and provide tarpaulins to cover the goods at all times and to protect against water ingress, snow, hail, dust, heat, sun and such.</p>
                      <p>The SUB-CONTRACTOR shall inform One Purpose Logistics (Pty) Ltd telephonically or by telefax of any unusual delay occasioned by any circumstance whatsoever whether before loading or during transit of the goods.</p>
                      <p>The SUB-CONTRACTOR shall provide access to tracking system that have to be installed in all vehicles provided to One Purpose Logistics (Pty) Ltd.</p>
                      <p>The goods shall be delivered to the place and upon the date and time stated on the load confirmation. The SUB-CONTRACTOR shall substitute another vehicle at its own expense to carry the goods to their destination in the event that any vehicle breaks down. Failure shall leave One Purpose Logistics (Pty) Ltd entitled to supply another vehicle and the SUB-CONTRACTOR shall pay any cost incurred by One Purpose Logistics (Pty) Ltd in so doing.</p>

                      <h3 className="text-lg font-bold text-navy">11. INSURANCE AND RISK OF CARRIAGE</h3>
                      <p>The goods will be carried at the sole risk of the SUB-CONTRACTOR and the SUB-CONTRACTOR shall affect comprehensive (all risks) insurance on the goods against all possible perils that the goods may be exposed to, and against all damage to or loss of that can be suffered by the owner of the goods or One Purpose Logistics (Pty) Ltd or the SUB-CONTRACTOR by reason of the fact that the goods are damaged or lost or destroyed whilst being carried.</p>
                      <p>The SUB-CONTRACTOR shall provide One Purpose Logistics (Pty) Ltd with copies of an insurance policy in respect of the carriage of the goods upon receipt of a written request from One Purpose Logistics (Pty) Ltd.</p>

                      <h3 className="text-lg font-bold text-navy">12. PAYMENT TERMS</h3>
                      <p>Please note that the cut-off date clause as stipulated hereunder has been included and now forms part of our General Terms and Conditions for Sub-Contractors when commencing service delivery.</p>
                      <p>Disputes to be logged in writing, email as well as invoiced. One Purpose Logistics (Pty) Ltd will not accept offsets on their accounts.</p>
                      <p>Please note that payments are effected via electronic fund transfer (EFT) from our Standard Bank Account. Please allow 1-2 working days after the payment date for the payment to appear on your account statement.</p>

                      <h3 className="text-lg font-bold text-navy">13. PAYMENT DATE</h3>
                      <p>The date on which payment for an invoice is due, is not determined by the invoice date, but by the date on which the original invoices is received in our office.</p>
                      <p>The cut-off date for receipt of original invoices with complete original documentation is the 25th of each month to qualify for payment in 30 days after date of statement.</p>
                      <p>Should invoices be received after the 25th, payment will be carried over to the following month (60 days after statement).</p>
                      <p>Every cut-off date missed will lead to payment being delayed by another 30 days.</p>
                      <p>Any services provided like fuel, or repairs, etc. will be invoiced in the month they occurred and have to be settled by the 7th of the following month, excepting fuel, which is pre-paid.</p>

                      <h3 className="text-lg font-bold text-navy">14. MAIL PROCEDURES</h3>
                      <p>To ensure security and traceability, please send all mail via a courier company, the Post Office registered mail facility or Speed Service and provide us with a tracking number prior to the expected delivery date.</p>
                      <p>It is your responsibility to determine maximum transit times and allow sufficient time for mail to be delivered. No exception will be made if your service provider fails to deliver mail before the cut-off date.</p>
                      <p>Invoices and load documentation that are lost in the mail or in transit, and thus not successfully delivered to our offices, will not be paid.</p>
                      <p>Faxed and/or copies of invoices and documentation will not qualify for payment and will only serve as confirmation of delivery.</p>

                      <h3 className="text-lg font-bold text-navy">15. SHORTAGES</h3>
                      <p>The Sub-Contractor shall be liable for all shortages on loads transported.</p>
                      <p>One Purpose Logistics (Pty) Ltd may, in its discretion, deduct the value of the shortages from remuneration due to the Sub-Contractor.</p>

                      <h3 className="text-lg font-bold text-navy">16. PERFORMANCE OF WORK</h3>
                      <p>While rendering the Deliverables on a Client site for which the Sub-Contractor is responsible in terms of OHSA 85 of 1993 as amended, the Sub-Contractor will comply with the said Act.</p>
                      <p>The Sub-Contractor shall whilst on the One Purpose Logistics (Pty) Ltd Client site have in operation, a valid and current Workman's Compensation certificate.</p>
                      <p>The Sub-Contractor will whilst on the One Purpose Logistics (Pty) Ltd Client site have in operation a valid 3rd party insurance policy to fulfil any liability that arises for property damage, injury, or death to any party.</p>
                      <p>All the Sub-Contractors staff, contractors and sub-contractors can be obliged to undergo induction before entering the One Purpose Logistics (Pty) Ltd Client site.</p>

                      <h3 className="text-lg font-bold text-navy">17. DELAYS IN LOADING OR DELIVERY</h3>
                      <p>Sub-Contractors shall load goods to be transported within a reasonable period of time after receipt of One Purpose Logistics (Pty) Ltd.'s Purchase Order.</p>
                      <p>Sub-Contractors shall deliver at the destination within a reasonable period of time after loading.</p>
                      <p>The reasonableness of loading and off-loading times shall be determined by One Purpose Logistics (Pty) Ltd.</p>

                      <h3 className="text-lg font-bold text-navy">18. RESPONSIBILITY AND LIABILITY</h3>
                      <p>Notwithstanding anything to the contrary contained herein, the Sub-Contractor shall be responsible for any loss, damage or delay caused by the loss, theft or damage to any cargo and cargo documents during the period that any such documents are in the possession of the Sub-Contractor, his servants, or agents as far as it relates to the negligence of the Sub-Contractor.</p>
                      <p>The Sub-Contractor shall hold harmless and indemnify One Purpose Logistics (Pty) for all responsibility and liability arising out of such loss, damage or delay as contained above.</p>

                      <h3 className="text-lg font-bold text-navy">19. LIABILITY</h3>
                      <p>Goods shall be carried at the sole risk of the Sub-Contractor.</p>
                      <p>The Sub-Contractor shall take out and maintain load insurance to the actual average value per load, as determined by One Purpose Logistics (Pty) Ltd with a minimum of R1,000,000 (one million rand) per load. The liability of the Sub-Contractor shall not be limited to actual loss of or damage to goods, but not shall include any consequential loss.</p>

                      <h3 className="text-lg font-bold text-navy">20. DRIVERS AND VEHICLES</h3>
                      <p>The Sub-Contractor warrants that duly licensed and qualified drivers are allocated to vehicles and those drivers will observe the delivery instructions accordingly.</p>
                      <p>The Sub-Contractor warrants that vehicles will comply with all criteria necessary for the safe and secure transportation of loads.</p>
                      <p>The Sub-Contractor shall insure that all vehicles are regularly maintained and kept in a good wording condition.</p>
                      <p>Sub-Contractors may not further sub-contract any transport order without the prior written consent of One Purpose Logistics (Pty) Ltd.</p>

                      <h3 className="text-lg font-bold text-navy">21. PROTECTION OF CLIENT RELATIONSHIPS</h3>
                      <p>It is material term of this agreement that as the Sub-Contractor is dealing with the clients and customers of One Purpose Logistics (Pty) Ltd, the Sub-Contractor agrees to be bound as follows:</p>
                      <p>That the Sub-Contractor may not and will not directly or indirectly or in any other contact, approach, or cause to be approached, intervene, or elicit any transport business or cartage of any nature from any and all clients and customers of One Purpose Logistics (Pty) Ltd for the period of 2 years after termination of this Agreement, either or individually or as manager, employee, consultant, advisor or assistant of any person, company, close corporation or other legal entity.</p>

                      <h3 className="text-lg font-bold text-navy">22. JURISDICTION</h3>
                      <p>This agreement shall be governed by the laws of the Republic of South Africa and the parties agree to the jurisdiction of the Magistrate's Court in regard to all matters arising out of this agreement, such jurisdiction, however, to be in addition to and not in substitution for the jurisdiction of any other competent court otherwise having jurisdiction.</p>

                      <div className="mt-6 pt-4 border-t border-gray-200">
                        <p className="text-center text-gray-500 text-xs">© 2026 One Purpose Logistics (Pty) Ltd. All rights reserved.</p>
                      </div>
                    </div>
                    <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 rounded-b-2xl flex justify-end">
                      <button
                        onClick={() => setShowTermsModal(false)}
                        className="px-6 py-2 bg-navy text-white rounded-lg hover:bg-green hover:text-navy transition-colors"
                      >
                        I Understand & Close
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-navy mb-2">📋 Review Your Application</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-gray-500">Company:</span> {formData.company_name}</div>
                  <div><span className="text-gray-500">Reg #:</span> {formData.registration_number}</div>
                  <div><span className="text-gray-500">Contact:</span> {formData.contact_person}</div>
                  <div><span className="text-gray-500">Email:</span> {formData.contact_email}</div>
                  <div><span className="text-gray-500">Tractors:</span> {formData.total_tractors}</div>
                  <div><span className="text-gray-500">Trailers:</span> {formData.total_trailers}</div>
                  <div><span className="text-gray-500">Drivers:</span> {formData.total_drivers}</div>
                  <div><span className="text-gray-500">Documents:</span> {Object.values(documentNames).filter(Boolean).length} uploaded</div>
                  <div><span className="text-gray-500">GIT Insurance:</span> {formData.git_insurance_provider ? '✅ Provided' : '❌ Missing'}</div>
                  <div><span className="text-gray-500">Public Liability:</span> {formData.public_liability_provider ? '✅ Provided' : '❌ Missing'}</div>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800 flex items-start gap-2">
                  <FaInfoCircle className="mt-0.5 flex-shrink-0" />
                  <span>
                    <strong>Important:</strong> Your application will be reviewed by our team. You will receive a notification once your application is approved or if we need more information.
                  </span>
                </p>
              </div>

              <div className="flex justify-between">
                <button
                  type="button"
                  onClick={prevStep}
                  className="px-6 py-2 border border-gray-300 rounded-lg font-medium hover:bg-gray-50"
                >
                  Previous
                </button>
                <button
                  type="submit"
                  disabled={loading || !captchaValid || !formData.accept_terms || passwordStrength < 5 || formData.password !== formData.confirm_password}
                  className="px-8 py-3 rounded-lg text-white font-medium flex items-center gap-2 disabled:opacity-50"
                  style={{ backgroundColor: '#00A86B' }}
                >
                  {loading ? <FaSpinner className="animate-spin" /> : <FaCheckCircle />}
                  {loading ? 'Submitting...' : 'Submit Application'}
                </button>
              </div>
            </div>
          )}
        </form>

        <div className="mt-8 text-center border-t pt-6">
          <p className="text-sm text-gray-500">
            Already have an account? <Link to="/login" className="text-green-600 hover:underline">Login here</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default SubbieSignup;